import React, { useState, useRef } from "react";
import { 
  Shield, 
  Lock, 
  Server, 
  Eye, 
  Clock, 
  Users, 
  Database,
  FileText,
  CheckCircle2,
  Download,
  Printer,
  Building2,
  AlertTriangle,
  Key,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { toast } from "sonner";

/**
 * SECURITY ONE-PAGER PDF GENERATOR
 * Generates a professional security whitepaper for hospital IT/CISO review
 */

const LOGO_URL = "/logos/sterilemate-white.png?v=4";

export default function SecurityWhitepaper() {
  const [generating, setGenerating] = useState(false);
  const printRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    setGenerating(true);
    toast.info("Preparing PDF for download...");
    
    // Use browser print to PDF
    setTimeout(() => {
      window.print();
      setGenerating(false);
      toast.success("Use 'Save as PDF' in the print dialog");
    }, 500);
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Control Bar - Hidden in Print */}
      <div className="print:hidden bg-slate-900 text-white p-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="font-semibold">Security Whitepaper Generator</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrint}
              className="border-slate-600 text-white hover:bg-slate-800"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button 
              onClick={handleDownload}
              disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Document */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none my-8 print:my-0">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-8 print:p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-8 h-8 text-emerald-400" />
                <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Security Architecture</span>
              </div>
              <h1 className="text-3xl font-bold">AskIsaac.io</h1>
              <p className="text-slate-300 mt-1">Hospital-Grade Security Whitepaper</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Document Version 1.0</p>
              <p className="text-sm text-slate-400">{currentDate}</p>
              <p className="text-xs text-slate-500 mt-2">CONFIDENTIAL</p>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="p-8 print:p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Executive Summary
          </h2>
          <p className="text-slate-600 leading-relaxed">
            AskIsaac.io operates on a <strong>Zero-Trust Architecture</strong>, treating every request as a potential 
            threat until verified through Multi-Factor Authentication (MFA) and Role-Based Access Control (RBAC). 
            This document outlines our comprehensive security protocols designed to meet and exceed healthcare 
            facility infrastructure requirements while maintaining HIPAA Administrative Safeguards compliance.
          </p>
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800 font-medium">
              ✓ Zero-Knowledge Authentication &nbsp;&nbsp; ✓ AES-256 Encryption &nbsp;&nbsp; ✓ HIPAA Administrative Safeguards
            </p>
          </div>
        </div>

        {/* Section 1: Data Protection */}
        <div className="p-8 print:p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            1. Data Protection & Encryption
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">Encryption at Rest</h3>
              <p className="text-sm text-slate-600">
                All facility data, including ticket descriptions, vendor quotes, and infrastructure blueprints, 
                are encrypted using <strong>AES-256 bit</strong> industry standards.
              </p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">Encryption in Transit</h3>
              <p className="text-sm text-slate-600">
                All data moving between client devices and servers is forced through <strong>TLS 1.3</strong> 
                (Transport Layer Security) to prevent interception.
              </p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Zero-Knowledge Passwords
            </h3>
            <p className="text-sm text-blue-800">
              We utilize <strong>Bcrypt with a Cost Factor of 12</strong>. No plain-text passwords exist in our 
              system; not even a SuperAdmin can view or "recover" a user's password. Password reset requires 
              authenticated token generation.
            </p>
          </div>
        </div>

        {/* Section 2: Communication Security */}
        <div className="p-8 print:p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            2. Communication Security
          </h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Asynchronous Mirroring</h3>
                <p className="text-sm text-slate-600">
                  Every message is instantly mirrored to our HIPAA-compliant, Texas-based database.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Immutable Audit Logs (WORM)</h3>
                <p className="text-sm text-slate-600">
                  Write Once, Read Many logic ensures communications cannot be edited or deleted, 
                  providing 100% accurate records for Joint Commission (TJC) audits.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Zero-PHI Policy</h3>
                <p className="text-sm text-slate-600">
                  Platform is strictly for Non-Clinical Facility Operations. Pattern detection 
                  flags sensitive data (SSN, etc.) if accidentally entered.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Access Control */}
        <div className="p-8 print:p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            3. Access Control & Governance
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-3 font-semibold text-slate-700">Control</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Implementation</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="p-3 font-medium">Multi-Factor Authentication</td>
                  <td className="p-3 text-slate-600">TOTP-based MFA with Google Authenticator/Authy, mandatory for Admin/Supplier</td>
                  <td className="p-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">ACTIVE</span></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">Role-Based Access (RBAC)</td>
                  <td className="p-3 text-slate-600">Department silos: Supply Chain, Facilities, Biomed, etc.</td>
                  <td className="p-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">ACTIVE</span></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">Session Timeout</td>
                  <td className="p-3 text-slate-600">30-minute inactivity auto-logout for clinical terminals</td>
                  <td className="p-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">ACTIVE</span></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">WORM Audit Logs</td>
                  <td className="p-3 text-slate-600">Immutable Write-Once-Read-Many logs with integrity hashing</td>
                  <td className="p-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">ACTIVE</span></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">PHI Detection</td>
                  <td className="p-3 text-slate-600">Real-time scanning for SSN, MRN, and patient data patterns</td>
                  <td className="p-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">ACTIVE</span></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">IP Allowlisting</td>
                  <td className="p-3 text-slate-600">Optional restriction to hospital network ranges</td>
                  <td className="p-3"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">AVAILABLE</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 4: Infrastructure */}
        <div className="p-8 print:p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-600" />
            4. Infrastructure & Continuity
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-600" />
                Data Residency
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  Texas-based primary infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  US-South Multi-AZ deployment
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  State procurement law compliant
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Database className="w-4 h-4 text-slate-600" />
                Disaster Recovery
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  Point-in-Time Recovery (PITR)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  Off-site encrypted backups
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  99.9% uptime SLA
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="p-8 print:p-6 border-b border-slate-200 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Technical Specifications
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4 font-mono text-sm">
            <div className="bg-white p-4 rounded border border-slate-200">
              <p className="text-slate-500 text-xs uppercase mb-1">Authentication</p>
              <p className="text-slate-900">Bcrypt Salting (Rounds: 12)</p>
            </div>
            <div className="bg-white p-4 rounded border border-slate-200">
              <p className="text-slate-500 text-xs uppercase mb-1">Encryption</p>
              <p className="text-slate-900">AES-256-GCM / TLS 1.3</p>
            </div>
            <div className="bg-white p-4 rounded border border-slate-200">
              <p className="text-slate-500 text-xs uppercase mb-1">Audit Logging</p>
              <p className="text-slate-900">WORM-compliant SQL Mirror</p>
            </div>
            <div className="bg-white p-4 rounded border border-slate-200">
              <p className="text-slate-500 text-xs uppercase mb-1">Infrastructure</p>
              <p className="text-slate-900">US-South (Texas) Multi-AZ</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 print:p-6 bg-slate-900 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">AskIsaac.io</p>
              <p className="text-sm text-slate-400"></p>
              <p className="text-xs text-slate-500 mt-1">Texas C-Corporation</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">HIPAA Administrative Safeguards</p>
              <p className="text-xs text-slate-500 mt-1">Document generated: {currentDate}</p>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-slate-700 text-center">
            <p className="text-xs text-slate-500">
              This document is confidential and intended for IT Security review purposes only.
              <br />
              For questions, contact: security@sterilemate.com
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:p-6 { padding: 1.5rem !important; }
          .print\\:my-0 { margin-top: 0 !important; margin-bottom: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
